import { PageHeader } from '@/components/patterns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Save,
  Send,
  Sun,
  Zap,
  Calendar,
  Leaf,
  TrendingUp,
  DollarSign,
  Building2,
  MapPin,
  Plug,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Ban,
  Lock,
} from 'lucide-react';
import { DialogoDeAcceso } from '@/features/quotes/components/dialogo-de-acceso';
import { AppLayout } from '@/components/layout/app-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils/cn';
import { useToast } from '@/lib/hooks/use-toast';
import { confirmar } from '@/components/ui/confirm-dialog';
import {
  DecisionDeAprobacionDialog,
  type AccionDeDecision,
} from '@/features/pricer-datec/components/DecisionDeAprobacionDialog';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { pricerSolarService } from '../services/pricer-solar.service';
import { ETIQUETA_DE_ESTADO, VARIANTE_DE_ESTADO } from '../lib/estado-solar';
import { SolarHistoryTimeline } from '../components/solar-history-timeline';
import type {
  PricerSolarQuote,
  SolarCatalog,
  SolarPayload,
  SolarResults,
} from '../types/pricer-solar.types';

const STEPS = [
  { num: 1, label: 'Cliente', icon: Building2 },
  { num: 2, label: 'Ubicación + Panel', icon: MapPin },
  { num: 3, label: 'Consumo', icon: Plug },
];

const TIPOS_TECHO = ['Loza (Cemento)', 'Teja Duralit', 'Teja Colonial', 'Calamina / Metálico'];
const ORIENTACIONES = ['Norte (Óptima)', 'Noreste', 'Noroeste', 'Este', 'Oeste'];

function fmtUsd(n: number): string {
  return `$ ${n.toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtBs(n: number): string {
  return `Bs ${n.toLocaleString('es-BO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const DEFAULT_PAYLOAD: SolarPayload = {
  clientName: '',
  clientPhone: '',
  clientEmail: '',
  clientCompany: '',
  clientAddress: '',
  ciudad: '',
  hspHoursDay: 0,
  tipoTecho: null,
  orientacion: 'Norte (Óptima)',
  panelW: 510,
  panelBrand: null,
  panelModel: null,
  consumoMensualKwh: 0,
  cargoFijoBs: null,
  tipoRed: 'Monofásico',
  tipoInversor: 'On Grid',
  tipoEstructura: null,
  areaTechoM2: null,
  precioKwh: null,
  aniosProyeccion: 25,
  notas: '',
};

// ── Gauge SVG ───────────────────────────────────────────────────────

function Gauge({
  value,
  max,
  label,
  formatter,
  goodAt = 'low',
  thresholds = [10, 13],
}: {
  value: number | null;
  max: number;
  label: string;
  formatter: (v: number) => string;
  goodAt?: 'low' | 'high';
  thresholds?: [number, number];
}) {
  const pct = value == null ? 0 : Math.min(value / max, 1);
  const arcLen = 157;
  const offset = arcLen - pct * arcLen;

  let color = 'hsl(var(--ok))';
  if (value != null) {
    if (goodAt === 'low') {
      color =
        value < thresholds[0]
          ? 'hsl(var(--ok))'
          : value < thresholds[1]
            ? 'hsl(var(--alerta))'
            : 'hsl(var(--bloqueo))';
    } else {
      color =
        value > thresholds[1]
          ? 'hsl(var(--ok))'
          : value > thresholds[0]
            ? 'hsl(var(--alerta))'
            : 'hsl(var(--bloqueo))';
    }
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <svg viewBox="0 0 140 90" width="140" height="90">
        <path
          d="M 20 80 A 50 50 0 0 1 120 80"
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth="12"
          strokeLinecap="round"
        />
        <path
          d="M 20 80 A 50 50 0 0 1 120 80"
          fill="none"
          stroke={color}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={arcLen}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset .6s ease, stroke .3s ease' }}
        />
        <text
          x="70"
          y="72"
          textAnchor="middle"
          fontSize="22"
          fontWeight="800"
          fill={color}
          style={{ transition: 'fill .3s ease' }}
        >
          {value == null ? '—' : formatter(value)}
        </text>
      </svg>
      <div className="text-xs text-muted-foreground font-semibold">{label}</div>
    </div>
  );
}

// ── Wizard page ─────────────────────────────────────────────────────

export function PricerSolarPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { toast } = useToast();
  const { user } = useAuth();

  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState<SolarCatalog | null>(null);
  const [payload, setPayload] = useState<SolarPayload>(DEFAULT_PAYLOAD);
  const [results, setResults] = useState<SolarResults | null>(null);
  const [loadedQuote, setLoadedQuote] = useState<PricerSolarQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mostrarAcceso, setMostrarAcceso] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState<
    null | 'approve' | 'reject' | 'adjust' | 'cancel'
  >(null);
  const [historyKey, setHistoryKey] = useState(0);

  const canApprove =
    user?.role === 'COMPANY_ADMIN' || user?.role === 'PLATFORM_ADMIN' || user?.role === 'APPROVER';

  // Load catalog + (if editing) quote
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cat = await pricerSolarService.getCatalog();
        if (cancelled) return;
        setCatalog(cat);

        if (id) {
          const q = await pricerSolarService.getQuote(id);
          if (cancelled) return;
          setLoadedQuote(q);
          setPayload({ ...DEFAULT_PAYLOAD, ...q.payload });
          setResults(q.resultsSnapshot);
        } else if (cat.panelOptions[0]) {
          const first = cat.panelOptions[0];
          setPayload((p) => ({
            ...p,
            panelW: first.watts,
            panelBrand: first.brand ?? null,
            panelModel: first.model ?? null,
          }));
        }
      } catch (e: any) {
        toast({ title: 'Error al cargar', description: e?.message, variant: 'destructive' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Debounced recalc cuando hay datos suficientes
  useEffect(() => {
    if (!catalog) return;
    const ready =
      payload.hspHoursDay > 0 &&
      payload.consumoMensualKwh > 0 &&
      payload.panelW > 0 &&
      payload.tipoRed;
    if (!ready) {
      // No borrar el snapshot guardado de la cotización si todavía no hay datos
      // suficientes — sólo limpiar en modo nuevo (sin loadedQuote).
      if (!loadedQuote) setResults(null);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const r = await pricerSolarService.calculate(payload);
        setResults(r);
      } catch {
        // ignore — usuario sigue escribiendo, mantenemos el último results
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [payload, catalog, loadedQuote]);

  const updateField = useCallback(
    <K extends keyof SolarPayload>(field: K, value: SolarPayload[K]) => {
      setPayload((p) => ({ ...p, [field]: value }));
    },
    []
  );

  // Validation por paso
  const canAdvance = useMemo(() => {
    if (step === 1)
      return payload.clientName.trim().length > 0 && (payload.clientPhone ?? '').trim().length > 0;
    if (step === 2) return payload.hspHoursDay > 0 && payload.tipoTecho && payload.panelW > 0;
    if (step === 3)
      return payload.consumoMensualKwh > 0 && (payload.cargoFijoBs ?? 0) >= 0 && !!payload.tipoRed;
    return true;
  }, [step, payload]);

  // Acciones
  const handleSave = async () => {
    if (!payload.clientName.trim()) {
      toast({ title: 'Falta el nombre del cliente', variant: 'destructive' });
      return;
    }
    if (!results) {
      toast({ title: 'Completá los datos para calcular antes de guardar', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (id) {
        const { quote } = await pricerSolarService.updateQuote(id, payload);
        setLoadedQuote(quote);
        setHistoryKey((k) => k + 1);
        toast({ title: 'Cotización actualizada', variant: 'success' });
      } else {
        const { quote } = await pricerSolarService.createQuote(payload);
        navigate(`/pricer-solar/${quote.id}`, { replace: true });
        toast({ title: `Cotización ${quote.code} creada`, variant: 'success' });
      }
    } catch (e: any) {
      toast({ title: 'Error al guardar', description: e?.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!loadedQuote) {
      toast({ title: 'Guardá la cotización primero', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const q = await pricerSolarService.submitQuote(loadedQuote.id);
      setLoadedQuote(q);
      setHistoryKey((k) => k + 1);
      toast({ title: 'Enviada a aprobación', variant: 'success' });
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  /*
    Aprobar, rechazar y pedir ajuste se deciden en el cuadro de la app (el mismo del Pricer Datec), no en el
    `prompt()` del navegador: aprobar admite un comentario opcional; rechazar y pedir ajuste exigen el motivo
    (el botón no se habilita sin él). Cancelar o cerrar el cuadro no decide nada.
  */
  const [decision, setDecision] = useState<AccionDeDecision | null>(null);

  const handleApprove = () => setDecision('approve');
  const handleReject = () => setDecision('reject');
  const handleRequestAdjustment = () => setDecision('adjust');

  const ejecutarDecision = async (accion: AccionDeDecision, comentario: string | undefined) => {
    if (!loadedQuote) return;
    const textos = {
      approve: { ok: 'Cotización aprobada', error: 'No se pudo aprobar' },
      reject: { ok: 'Cotización rechazada', error: 'No se pudo rechazar' },
      adjust: { ok: 'Ajuste solicitado', error: 'No se pudo solicitar ajuste' },
    }[accion];
    setDecisionLoading(accion);
    try {
      const q =
        accion === 'approve'
          ? await pricerSolarService.approveQuote(loadedQuote.id, comentario)
          : accion === 'reject'
            ? await pricerSolarService.rejectQuote(loadedQuote.id, comentario ?? '')
            : await pricerSolarService.requestAdjustment(loadedQuote.id, comentario ?? '');
      setLoadedQuote(q);
      setHistoryKey((k) => k + 1);
      setDecision(null);
      toast({ title: textos.ok, variant: 'success' });
    } catch (e: any) {
      toast({
        title: textos.error,
        description: e?.response?.data?.error || e?.message,
        variant: 'destructive',
      });
    } finally {
      setDecisionLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!loadedQuote) return;
    const seguro = await confirmar({
      title: `¿Cancelar la cotización ${loadedQuote.code}?`,
      confirmText: 'Cancelar cotización',
      cancelText: 'Volver',
      variant: 'destructive',
    });
    if (!seguro) return;
    setDecisionLoading('cancel');
    try {
      const q = await pricerSolarService.cancelQuote(loadedQuote.id);
      setLoadedQuote(q);
      setHistoryKey((k) => k + 1);
      toast({ title: 'Cotización cancelada', variant: 'success' });
    } catch (e: any) {
      toast({
        title: 'No se pudo cancelar',
        description: e?.response?.data?.error || e?.message,
        variant: 'destructive',
      });
    } finally {
      setDecisionLoading(null);
    }
  };

  const readOnly =
    loadedQuote != null &&
    loadedQuote.status !== 'DRAFT' &&
    loadedQuote.status !== 'ADJUSTMENT_REQUESTED';

  // Chart data
  const chartData = useMemo(() => {
    if (!results) return [];
    return [
      { anio: 0, acumulado: 0, inversion: results.invNetaBs },
      ...results.proyeccion.map((p) => ({
        anio: p.anio,
        acumulado: p.ahorroAcumuladoBs,
        inversion: results.invNetaBs,
      })),
    ];
  }, [results]);

  if (loading) {
    return (
      <AppLayout>
        <div className="container mx-auto p-8 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
        {/* El encabezado de Cotiza (rediseño, 19-oct): «Historial» vuelve a la lista. */}
        <PageHeader
          modulo="cotiza"
          etiqueta="Cotiza · Cotizador Solar"
          icono={Sun}
          titulo={
            <span className="inline-flex flex-wrap items-center gap-3">
              {loadedQuote ? loadedQuote.code : 'Nueva cotización solar'}
              {loadedQuote && (
                <Badge variant={VARIANTE_DE_ESTADO[loadedQuote.status] ?? 'default'}>
                  {ETIQUETA_DE_ESTADO[loadedQuote.status] ?? loadedQuote.status}
                </Badge>
              )}
            </span>
          }
          subtitulo="Sistema fotovoltaico On-Grid"
          volver={{ texto: 'Volver al historial', onClick: () => navigate('/pricer-solar') }}
          acciones={
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {loadedQuote && (
                <Button variant="outline" onClick={() => setMostrarAcceso(true)}>
                  <Lock className="w-4 h-4 mr-1.5" />
                  Acceso
                </Button>
              )}
              {!readOnly && (
                <Button variant="outline" onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-1.5" />
                  )}
                  Guardar
                </Button>
              )}
              {loadedQuote && !readOnly && (
                <Button onClick={handleSubmit} disabled={submitting || !results}>
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-1.5" />
                  )}
                  Enviar a aprobación
                </Button>
              )}
              {loadedQuote?.status === 'PENDING_APPROVAL' && canApprove && (
                <>
                  <Button
                    variant="outline"
                    className="border-ok-borde text-ok hover:bg-ok-suave"
                    onClick={handleApprove}
                    disabled={!!decisionLoading}
                  >
                    {decisionLoading === 'approve' ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-1.5" />
                    )}
                    Aprobar
                  </Button>
                  <Button
                    variant="outline"
                    className="border-alerta-borde text-alerta hover:bg-alerta-suave"
                    onClick={handleRequestAdjustment}
                    disabled={!!decisionLoading}
                  >
                    {decisionLoading === 'adjust' ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 mr-1.5" />
                    )}
                    Solicitar ajuste
                  </Button>
                  <Button
                    variant="outline"
                    className="border-bloqueo-borde text-bloqueo hover:bg-bloqueo-suave"
                    onClick={handleReject}
                    disabled={!!decisionLoading}
                  >
                    {decisionLoading === 'reject' ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <XCircle className="w-4 h-4 mr-1.5" />
                    )}
                    Rechazar
                  </Button>
                </>
              )}
              {loadedQuote &&
                ['DRAFT', 'PENDING_APPROVAL', 'ADJUSTMENT_REQUESTED'].includes(
                  loadedQuote.status
                ) && (
                  <Button
                    variant="ghost"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={handleCancel}
                    disabled={!!decisionLoading}
                    title="Cancelar cotización"
                  >
                    {decisionLoading === 'cancel' ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <Ban className="w-4 h-4 mr-1.5" />
                    )}
                    Cancelar
                  </Button>
                )}
            </div>
          }
        />

        {/* STEPS */}
        <div className="flex items-center justify-center gap-2 max-w-2xl mx-auto">
          {STEPS.map((s) => {
            const isActive = step === s.num;
            const isDone = step > s.num;
            const Icon = s.icon;
            return (
              <button
                key={s.num}
                onClick={() => setStep(s.num)}
                className={cn(
                  'flex-1 flex items-center gap-2 px-4 py-3 rounded-lg border transition-all text-left',
                  isActive && 'border-primary bg-primary/5',
                  isDone && 'border-ok bg-ok-suave',
                  !isActive && !isDone && 'border-border hover:border-primary/40'
                )}
              >
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-bold',
                    isActive && 'bg-primary text-primary-foreground',
                    isDone && 'bg-ok text-primary-foreground',
                    !isActive && !isDone && 'bg-muted text-muted-foreground'
                  )}
                >
                  {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground">Paso {s.num}</div>
                  <div className="text-sm font-semibold truncate">{s.label}</div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-6">
          {/* FORMULARIO */}
          <Card>
            <CardHeader>
              <CardTitle>
                Paso {step}: {STEPS[step - 1].label}
              </CardTitle>
              <CardDescription>
                {step === 1 && 'Datos del cliente para personalizar la propuesta.'}
                {step === 2 && 'Determina las Horas Sol Pico (HSP) y el panel a utilizar.'}
                {step === 3 &&
                  'El consumo mensual es el dato más crítico — pedile la factura de los últimos 3 meses.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {step === 1 && (
                <Step1Client payload={payload} update={updateField} disabled={readOnly} />
              )}
              {step === 2 && catalog && (
                <Step2Location
                  payload={payload}
                  update={updateField}
                  catalog={catalog}
                  disabled={readOnly}
                />
              )}
              {step === 3 && catalog && (
                <Step3Consumption
                  payload={payload}
                  update={updateField}
                  disabled={readOnly}
                  catalog={catalog}
                />
              )}

              <div className="flex items-center justify-between pt-4 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep((s) => Math.max(1, s - 1))}
                  disabled={step === 1}
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Anterior
                </Button>
                {step < 3 ? (
                  <Button
                    size="sm"
                    onClick={() => setStep((s) => Math.min(3, s + 1))}
                    disabled={!canAdvance}
                  >
                    Siguiente
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button size="sm" onClick={handleSave} disabled={saving || !results}>
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-1.5" />
                    )}
                    Guardar cotización
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* PREVIEW EN VIVO */}
          <div className="space-y-4">
            {/* Sistema dimensionado */}
            <Card className="bg-gradient-to-br from-alerta-suave to-alerta-suave border-alerta-borde/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sun className="w-4 h-4 text-alerta" />
                  Sistema recomendado
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-2xl font-black tracking-tight text-alerta">
                      {results ? results.kwp.toFixed(2) : '—'}
                    </p>
                    <p className="text-xs text-muted-foreground font-semibold">kWp</p>
                  </div>
                  <div>
                    <p className="text-2xl font-black tracking-tight text-alerta">
                      {results ? `${results.inversorKw}` : '—'}
                      {results?.inversorIsTrifasico && <span className="text-xs ml-1">3Φ</span>}
                    </p>
                    <p className="text-xs text-muted-foreground font-semibold">kW inversor</p>
                  </div>
                  <div>
                    <p className="text-2xl font-black tracking-tight text-alerta">
                      {results ? results.totalPaneles : '—'}
                    </p>
                    <p className="text-xs text-muted-foreground font-semibold">
                      paneles {results ? `× ${results.panelW}W` : ''}
                    </p>
                  </div>
                </div>
                {results?.inverter && (
                  <div className="mt-3 pt-3 border-t border-alerta-borde/60 text-xs space-y-0.5">
                    <p className="font-semibold text-alerta">
                      Inversor: {results.inverter.brand} {results.inverter.model}
                      {results.inverter.priceUsd != null && (
                        <span className="ml-2 text-ok font-medium">
                          USD {results.inverter.priceUsd}
                        </span>
                      )}
                    </p>
                    <p className="text-muted-foreground">
                      {results.inverter.tipo} · {results.inverter.fase} · {results.inverter.kw}kW
                      {!results.inverter.fromCatalog && (
                        <span className="ml-2 text-alerta/80 italic">
                          (genérico, no sale del catálogo)
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 4 KPIs */}
            <div className="grid grid-cols-2 gap-3">
              <KpiCard
                icon={<DollarSign className="w-4 h-4" />}
                label="Inversión neta"
                value={results ? fmtBs(results.invNetaBs) : '—'}
                hint={results ? fmtUsd(results.invNetaUsd) : ''}
                tone="indigo"
              />
              <KpiCard
                icon={<TrendingUp className="w-4 h-4" />}
                label="Ahorro anual"
                value={results ? fmtBs(results.ahorroAnualBs) : '—'}
                hint="con precio kWh actual"
                tone="emerald"
              />
              <KpiCard
                icon={<Zap className="w-4 h-4" />}
                label="Generación anual"
                value={
                  results
                    ? `${results.generacionAnualKwh.toLocaleString('es-BO', { maximumFractionDigits: 0 })} kWh`
                    : '—'
                }
                tone="amber"
              />
              <KpiCard
                icon={<Leaf className="w-4 h-4" />}
                label="CO₂ evitado"
                value={results ? `${results.co2EvitadoTon.toFixed(2)} ton/año` : '—'}
                tone="emerald"
              />
            </div>

            {/* Gauges ROI / TIR */}
            <Card>
              <CardContent className="p-5">
                <div className="grid grid-cols-2 gap-4">
                  <Gauge
                    value={results?.roiAnios ?? null}
                    max={15}
                    label={`ROI (años)`}
                    formatter={(v) => v.toFixed(1)}
                    goodAt="low"
                    thresholds={[10, 13]}
                  />
                  <Gauge
                    value={results?.tirPorcentaje ?? null}
                    max={20}
                    label="TIR (%)"
                    formatter={(v) => `${v.toFixed(1)}%`}
                    goodAt="high"
                    thresholds={[5, 8]}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* COMPROBANTE DE MATERIALES (full width) */}
        {results?.breakdown && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Comprobante de materiales
                </span>
                {!results.breakdown.complete && (
                  <span className="text-xs font-normal text-alerta">
                    ⚠ Faltan precios en el catálogo — usando estimación por USD/kWp
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground border-b">
                    <tr>
                      <th className="text-left py-2">Material</th>
                      <th className="text-right py-2">Cant.</th>
                      <th className="text-right py-2">Precio U. (USD)</th>
                      <th className="text-right py-2">Total USD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.breakdown.lines.map((l, i) => (
                      <tr key={i} className="border-b last:border-b-0">
                        <td className="py-2">{l.material}</td>
                        <td className="py-2 text-right font-mono">{l.qty}</td>
                        <td className="py-2 text-right font-mono">
                          {l.priceUsdUnit ? fmtUsd(l.priceUsdUnit) : '—'}
                        </td>
                        <td className="py-2 text-right font-mono">
                          {l.totalUsd ? fmtUsd(l.totalUsd) : '—'}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 bg-muted/30 font-semibold">
                      <td className="py-2.5" colSpan={3}>
                        Total
                      </td>
                      <td className="py-2.5 text-right font-mono text-lg">
                        {fmtUsd(results.breakdown.totalUsd)}
                      </td>
                    </tr>
                    <tr className="bg-muted/15 text-muted-foreground">
                      <td className="py-1.5" colSpan={3}>
                        Equivalente en Bs (TC del cálculo)
                      </td>
                      <td className="py-1.5 text-right font-mono">
                        {fmtBs(results.breakdown.totalBs)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* DESGLOSE FINANCIERO + CHART (full width) */}
        {results && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Desglose financiero
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground border-b">
                      <tr>
                        <th className="text-left py-2 pr-3">Concepto</th>
                        <th className="text-right py-2 px-3">USD</th>
                        <th className="text-right py-2 pl-3">BOB</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr>
                        <td className="py-2 pr-3">Inversión bruta</td>
                        <td className="text-right font-mono py-2 px-3">
                          {fmtUsd(results.invBrutaUsd)}
                        </td>
                        <td className="text-right font-mono py-2 pl-3">
                          {fmtBs(results.invBrutaBs)}
                        </td>
                      </tr>
                      <tr className="text-ok">
                        <td className="py-2 pr-3">− Crédito fiscal (IVA 13%)</td>
                        <td className="text-right font-mono py-2 px-3">
                          − {fmtUsd(results.creditoIvaUsd)}
                        </td>
                        <td className="text-right font-mono py-2 pl-3">
                          − {fmtBs(results.creditoIvaBs)}
                        </td>
                      </tr>
                      <tr className="font-bold border-t-2">
                        <td className="py-3 pr-3">Inversión neta</td>
                        <td className="text-right font-mono py-3 px-3">
                          {fmtUsd(results.invNetaUsd)}
                        </td>
                        <td className="text-right font-mono py-3 pl-3">
                          {fmtBs(results.invNetaBs)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Proyección de ahorro a {results.aniosProyeccion} años
                </CardTitle>
                <CardDescription>
                  Curva con degradación del 0.5% anual del panel. El cruce con la línea de inversión
                  = payback.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <ComposedChart
                      data={chartData}
                      margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                      <XAxis
                        dataKey="anio"
                        tick={{ fontSize: 12 }}
                        label={{
                          value: 'Año',
                          position: 'insideBottomRight',
                          offset: -2,
                          fontSize: 12,
                        }}
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}K` : String(v))}
                      />
                      <Tooltip
                        formatter={(v: any) => fmtBs(Number(v))}
                        labelFormatter={(l) => `Año ${l}`}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <defs>
                        <linearGradient id="gA" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="hsl(var(--ok))" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="hsl(var(--ok))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area
                        type="monotone"
                        dataKey="acumulado"
                        name="Ahorro acumulado"
                        stroke="hsl(var(--ok))"
                        fill="url(#gA)"
                        strokeWidth={2.5}
                      />
                      <Line
                        type="linear"
                        dataKey="inversion"
                        name="Inversión neta"
                        stroke="hsl(var(--ia))"
                        strokeWidth={2}
                        strokeDasharray="6 3"
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tabla año a año</CardTitle>
                <CardDescription>Generación, ahorro anual y acumulado proyectado.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto max-h-[400px]">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground border-b sticky top-0 bg-card">
                      <tr>
                        <th className="text-left py-2 pr-3">Año</th>
                        <th className="text-right py-2 px-3">Generación (kWh)</th>
                        <th className="text-right py-2 px-3">Ahorro Bs</th>
                        <th className="text-right py-2 pl-3">Acumulado Bs</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {results.proyeccion.map((p) => (
                        <tr
                          key={p.anio}
                          className={p.ahorroAcumuladoBs >= results.invNetaBs ? 'bg-ok-suave' : ''}
                        >
                          <td className="py-1.5 pr-3 font-mono">{p.anio}</td>
                          <td className="text-right font-mono py-1.5 px-3">
                            {p.generacionKwh.toLocaleString('es-BO', { maximumFractionDigits: 0 })}
                          </td>
                          <td className="text-right font-mono py-1.5 px-3">
                            {fmtBs(p.ahorroAnualBs)}
                          </td>
                          <td className="text-right font-mono py-1.5 pl-3 font-semibold">
                            {fmtBs(p.ahorroAcumuladoBs)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Historial — sólo cuando la cotización está guardada */}
        {loadedQuote && <SolarHistoryTimeline quoteId={loadedQuote.id} refreshKey={historyKey} />}
      </div>

      {loadedQuote && (
        <DialogoDeAcceso
          abierto={mostrarAcceso}
          onCerrar={() => setMostrarAcceso(false)}
          quoteId={loadedQuote.id}
          tipo="PRICER_SOLAR"
        />
      )}

      <DecisionDeAprobacionDialog
        accion={decision}
        codigo={loadedQuote?.code}
        enviando={decisionLoading !== null}
        onConfirmar={(comentario) => decision && ejecutarDecision(decision, comentario)}
        onCancelar={() => setDecision(null)}
      />
    </AppLayout>
  );
}

// ── KPI card ───────────────────────────────────────────────────────

function KpiCard({
  icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  tone: 'indigo' | 'emerald' | 'amber';
}) {
  const colors = {
    indigo: 'text-info',
    emerald: 'text-ok',
    amber: 'text-alerta',
  };
  return (
    <Card>
      <CardContent className="p-4">
        <div className={cn('flex items-center gap-1.5 text-xs font-semibold', colors[tone])}>
          {icon}
          {label}
        </div>
        <p className="text-xl font-bold tracking-tight mt-1.5 truncate">{value}</p>
        {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
      </CardContent>
    </Card>
  );
}

// ── Step components ─────────────────────────────────────────────────

function Step1Client({
  payload,
  update,
  disabled,
}: {
  payload: SolarPayload;
  update: <K extends keyof SolarPayload>(field: K, value: SolarPayload[K]) => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label>Nombre completo *</Label>
        <Input
          value={payload.clientName}
          onChange={(e) => update('clientName', e.target.value)}
          placeholder="Ej: Juan Pérez García"
          disabled={disabled}
        />
      </div>
      <div>
        <Label>Teléfono / WhatsApp *</Label>
        <Input
          value={payload.clientPhone ?? ''}
          onChange={(e) => update('clientPhone', e.target.value)}
          placeholder="+591 7 1234567"
          disabled={disabled}
        />
      </div>
      <div>
        <Label>Correo electrónico</Label>
        <Input
          type="email"
          value={payload.clientEmail ?? ''}
          onChange={(e) => update('clientEmail', e.target.value)}
          placeholder="cliente@email.com"
          disabled={disabled}
        />
      </div>
      <div>
        <Label>Empresa / Razón social</Label>
        <Input
          value={payload.clientCompany ?? ''}
          onChange={(e) => update('clientCompany', e.target.value)}
          placeholder="(opcional)"
          disabled={disabled}
        />
      </div>
      <div className="col-span-2">
        <Label>Dirección / Referencia</Label>
        <Input
          value={payload.clientAddress ?? ''}
          onChange={(e) => update('clientAddress', e.target.value)}
          placeholder="Av. Ejemplo 123, Barrio X"
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function Step2Location({
  payload,
  update,
  catalog,
  disabled,
}: {
  payload: SolarPayload;
  update: <K extends keyof SolarPayload>(field: K, value: SolarPayload[K]) => void;
  catalog: SolarCatalog;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label>Ciudad *</Label>
          <Select
            value={payload.ciudad}
            onValueChange={(v) => {
              const city = catalog.cities.find((c) => c.name === v);
              update('ciudad', v);
              if (city) update('hspHoursDay', city.hspHoursDay);
            }}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar..." />
            </SelectTrigger>
            <SelectContent>
              {catalog.cities.map((c) => (
                <SelectItem key={c.name} value={c.name}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {payload.hspHoursDay > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              HSP: {payload.hspHoursDay.toFixed(2)} h/día
            </p>
          )}
        </div>
        <div>
          <Label>Tipo de techo *</Label>
          <Select
            value={payload.tipoTecho ?? ''}
            onValueChange={(v) => update('tipoTecho', v)}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar..." />
            </SelectTrigger>
            <SelectContent>
              {TIPOS_TECHO.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Orientación</Label>
          <Select
            value={payload.orientacion ?? 'Norte (Óptima)'}
            onValueChange={(v) => update('orientacion', v)}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ORIENTACIONES.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Panel solar *</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mt-1.5">
          {catalog.panelOptions.map((p, idx) => {
            const selected =
              payload.panelW === p.watts &&
              (payload.panelBrand ?? '') === (p.brand ?? '') &&
              (payload.panelModel ?? '') === (p.model ?? '');
            return (
              <button
                key={`${p.watts}-${p.brand ?? ''}-${p.model ?? ''}-${idx}`}
                type="button"
                onClick={() => {
                  if (disabled) return;
                  update('panelW', p.watts);
                  update('panelBrand', p.brand ?? null);
                  update('panelModel', p.model ?? null);
                }}
                disabled={disabled}
                className={cn(
                  'p-3 rounded-lg border-2 text-center transition-all',
                  selected
                    ? 'border-alerta bg-alerta-suave ring-1 ring-alerta'
                    : 'border-border hover:border-alerta-borde hover:bg-alerta-suave/50',
                  disabled && 'opacity-60 cursor-not-allowed'
                )}
              >
                <div className={cn('text-lg font-bold', selected && 'text-alerta')}>{p.watts}W</div>
                <div className="text-xs text-muted-foreground truncate">
                  {p.brand ? `${p.brand}${p.model ? ' · ' + p.model : ''}` : p.label}
                </div>
                {p.priceUsd != null && (
                  <div className="text-xs text-ok font-medium mt-0.5">USD {p.priceUsd}</div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Step3Consumption({
  payload,
  update,
  disabled,
  catalog,
}: {
  payload: SolarPayload;
  update: <K extends keyof SolarPayload>(field: K, value: SolarPayload[K]) => void;
  disabled?: boolean;
  catalog: SolarCatalog;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-alerta-suave border border-alerta-borde p-3 text-xs text-alerta">
        ⚡ El consumo mensual es el dato más crítico. Pedile al cliente el promedio de los últimos 3
        meses de su factura eléctrica.
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label>Consumo mensual (kWh) *</Label>
          <Input
            type="number"
            min={0}
            value={payload.consumoMensualKwh || ''}
            onChange={(e) => update('consumoMensualKwh', parseFloat(e.target.value) || 0)}
            placeholder="350"
            disabled={disabled}
          />
        </div>
        <div>
          <Label>Cargo fijo factura (Bs) *</Label>
          <Input
            type="number"
            min={0}
            value={payload.cargoFijoBs ?? ''}
            onChange={(e) => update('cargoFijoBs', parseFloat(e.target.value) || 0)}
            placeholder="150"
            disabled={disabled}
          />
        </div>
        <div>
          <Label>Tipo de red *</Label>
          <Select
            value={payload.tipoRed}
            onValueChange={(v) => update('tipoRed', v as SolarPayload['tipoRed'])}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Monofásico">Monofásico (1F)</SelectItem>
              <SelectItem value="Trifásico">Trifásico (3F)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Tipo de inversor</Label>
          <Select
            value={payload.tipoInversor ?? 'On Grid'}
            onValueChange={(v) =>
              update('tipoInversor', v as NonNullable<SolarPayload['tipoInversor']>)
            }
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="On Grid">On Grid</SelectItem>
              <SelectItem value="Híbrido">Híbrido</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {catalog.structureCatalog && catalog.structureCatalog.length > 0 && (
          <div>
            <Label>Estructura de montaje</Label>
            <Select
              value={payload.tipoEstructura ?? '__none__'}
              onValueChange={(v) => update('tipoEstructura', v === '__none__' ? null : v)}
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Elegí un tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin estructura</SelectItem>
                {catalog.structureCatalog.map((s) => (
                  <SelectItem key={s.name} value={s.name}>
                    {s.name} — USD {s.priceUsd}/kit
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label>Área disponible techo (m²)</Label>
          <Input
            type="number"
            min={0}
            value={payload.areaTechoM2 ?? ''}
            onChange={(e) => update('areaTechoM2', parseFloat(e.target.value) || null)}
            placeholder="40"
            disabled={disabled}
          />
        </div>
        <div>
          <Label>Precio kWh (Bs)</Label>
          <Input
            type="number"
            step="0.01"
            min={0}
            value={payload.precioKwh ?? ''}
            onChange={(e) => update('precioKwh', parseFloat(e.target.value) || null)}
            placeholder="0.85"
            disabled={disabled}
          />
        </div>
        <div>
          <Label>Años de proyección</Label>
          <Select
            value={String(payload.aniosProyeccion ?? 25)}
            onValueChange={(v) => update('aniosProyeccion', parseInt(v))}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 15, 20, 25].map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y} años
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Notas / Observaciones</Label>
        <Textarea
          value={payload.notas ?? ''}
          onChange={(e) => update('notas', e.target.value)}
          placeholder="Ej: Cliente interesado en financiamiento, requiere montaje en techo inclinado..."
          rows={3}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
